import {
  createAccountAwarePrivacyControlsRepository,
  decodePrivacyControlsProjectionV1,
  validConsentNoticeVersion,
} from "./account/privacy-controls-repository.js";
import { browserAccountStateSessionRepository } from "./account/account-state-session-repository.js";
import {
  decodeTelemetrySelection,
  disabledTelemetrySelection,
  telemetrySelectionActive,
} from "./telemetry-selection-contract.js";

export const PRIVACY_CONTROLS_STORAGE_KEY = "fishystuff.privacy-controls.v1";
export const TELEMETRY_NOTICE_VERSION = "optional-telemetry.v2";
export const DISCORD_IMAGE_NOTICE_VERSION = "discord-images.v1";

const DOCUMENT_VERSION = 1;
const LOCAL_BRAND = Symbol.for("fishystuff.local-privacy-controls-repository.v1");
const BROWSER_LOCAL_BRAND = Symbol.for("fishystuff.browser-local-privacy-controls-repository.v1");
const BROWSER_BRAND = Symbol.for("fishystuff.browser-privacy-controls-repository.v1");

function freeze(value) {
  return Object.freeze({ ...value });
}

function unavailable(operation, reason) {
  return freeze({ kind: "unavailable", operation, reason });
}

function decodeStored(raw) {
  if (typeof raw !== "string") return freeze({ kind: "invalid", reason: "stored-value-must-be-string" });
  let value;
  try {
    value = JSON.parse(raw);
  } catch (_error) {
    return freeze({ kind: "invalid", reason: "malformed-json" });
  }
  if (
    value
    && typeof value === "object"
    && Number.isSafeInteger(value.schemaVersion)
    && value.schemaVersion > DOCUMENT_VERSION
  ) {
    return freeze({ kind: "unsupportedVersion", foundVersion: value.schemaVersion });
  }
  if (
    value?.kind === "ready"
    && value.schemaVersion === 1
    && !Object.hasOwn(value, "telemetrySelectionApproved")
  ) {
    value = { ...value, telemetrySelectionApproved: false };
  }
  if (value?.kind === "ready" && value.schemaVersion === 1) {
    const receipt = value.telemetryConsent;
    const decision = receipt?.decision;
    const telemetryConsent = receipt && decision
      ? {
          ...receipt,
          decision: {
            kind: decision.kind,
            noticeVersion: decision.noticeVersion,
          },
        }
      : receipt;
    const { telemetryAgeConfirmation: _retiredAge, ...current } = value;
    value = { ...current, telemetryConsent };
  }
  try {
    return decodePrivacyControlsProjectionV1(value);
  } catch (_error) {
    return freeze({ kind: "invalid", reason: "privacy-controls-document-is-invalid" });
  }
}

function validatedMutation(mutation) {
  if (!mutation || typeof mutation !== "object" || Array.isArray(mutation)) {
    throw new TypeError("Privacy-controls mutation must be an object.");
  }
  if (mutation.kind === "telemetrySelection") {
    if (Object.keys(mutation).length !== 2) {
      throw new TypeError("Telemetry selection mutation is invalid.");
    }
    return freeze({
      kind: mutation.kind,
      selection: decodeTelemetrySelection(mutation.selection),
    });
  }
  if (mutation.kind === "telemetrySelectionApproval") {
    if (Object.keys(mutation).length !== 2 || typeof mutation.approved !== "boolean") {
      throw new TypeError("Telemetry selection approval mutation is invalid.");
    }
    return freeze({ kind: mutation.kind, approved: mutation.approved });
  }
  if (mutation.kind === "telemetryConsent") {
    if (Object.keys(mutation).length !== 2) {
      throw new TypeError("Telemetry consent mutation is invalid.");
    }
    const decision = mutation.decision;
    const decisionFields = decision && typeof decision === "object"
      ? Object.keys(decision).sort()
      : [];
    const expectedDecisionFields = ["kind", "noticeVersion"];
    if (
      !decision
      || typeof decision !== "object"
      || !["granted", "denied"].includes(decision.kind)
      || decisionFields.length !== expectedDecisionFields.length
      || decisionFields.some((field, index) => field !== expectedDecisionFields[index])
      || !validConsentNoticeVersion(decision.noticeVersion)
    ) {
      throw new TypeError("Telemetry consent decision is invalid.");
    }
    return freeze({ kind: mutation.kind, decision: freeze(decision) });
  }
  if (mutation.kind === "discordImageConsent") {
    if (
      Object.keys(mutation).length !== 2
      || !mutation.decision
      || typeof mutation.decision !== "object"
      || Object.keys(mutation.decision).length !== 2
      || !["granted", "denied"].includes(mutation.decision.kind)
      || !validConsentNoticeVersion(mutation.decision.noticeVersion)
    ) {
      throw new TypeError("Discord-image consent decision is invalid.");
    }
    return freeze({ kind: mutation.kind, decision: freeze(mutation.decision) });
  }
  throw new TypeError("Privacy-controls mutation kind is invalid.");
}

function mutationSatisfied(projection, mutation) {
  if (projection.kind !== "ready") return false;
  if (mutation.kind === "telemetrySelection") {
    return JSON.stringify(projection.telemetrySelection) === JSON.stringify(mutation.selection)
      && projection.telemetrySelectionApproved === false;
  }
  if (mutation.kind === "telemetrySelectionApproval") {
    return projection.telemetrySelectionApproved === mutation.approved;
  }
  const receipt = mutation.kind === "telemetryConsent"
    ? projection.telemetryConsent
    : projection.discordImageConsent;
  return JSON.stringify(receipt?.decision) === JSON.stringify(mutation.decision);
}

export function createPrivacyControlsLocalRepository({
  storage = null,
  eventTarget = null,
  nowUnixMs = () => Date.now(),
} = {}) {
  let currentState = freeze({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();
  const state = () => currentState;
  const publish = (value) => {
    const next = freeze(value);
    if (JSON.stringify(currentState) === JSON.stringify(next)) return currentState;
    currentState = next;
    if (!disposed) for (const listener of listeners) listener(currentState);
    return currentState;
  };
  const read = () => {
    try {
      if (!storage || typeof storage.getItem !== "function") throw new Error();
      const raw = storage.getItem(PRIVACY_CONTROLS_STORAGE_KEY);
      return raw === null
        ? freeze({ kind: "missing", schemaVersion: DOCUMENT_VERSION })
        : decodeStored(raw);
    } catch (_error) {
      return unavailable("read", "storage-read-failed");
    }
  };
  const load = () => publish(read());

  async function mutateAt(input, decidedAtUnixMs = null) {
    const mutation = validatedMutation(input);
    const authoritative = read();
    if (!['missing', 'ready'].includes(authoritative.kind)) return publish(authoritative);
    if (mutationSatisfied(authoritative, mutation)) return publish(authoritative);
    const now = decidedAtUnixMs ?? nowUnixMs();
    if (!Number.isSafeInteger(now) || now <= 0) {
      return publish(unavailable("write", "clock-produced-invalid-time"));
    }
    const next = {
      kind: "ready",
      schemaVersion: DOCUMENT_VERSION,
      revision: authoritative.kind === "ready" ? authoritative.revision + 1 : 1,
      telemetryConsent: authoritative.kind === "ready" ? authoritative.telemetryConsent : null,
      telemetrySelection: authoritative.kind === "ready"
        ? authoritative.telemetrySelection
        : disabledTelemetrySelection(),
      telemetrySelectionApproved: authoritative.kind === "ready"
        ? authoritative.telemetrySelectionApproved
        : false,
      discordImageConsent: authoritative.kind === "ready"
        ? authoritative.discordImageConsent
        : null,
    };
    if (!Number.isSafeInteger(next.revision)) {
      return publish(unavailable("write", "revision-overflow"));
    }
    if (mutation.kind === "telemetrySelection") {
      next.telemetrySelection = mutation.selection;
      next.telemetrySelectionApproved = false;
    } else if (mutation.kind === "telemetrySelectionApproval") {
      next.telemetrySelectionApproved = mutation.approved;
    } else {
      next[mutation.kind] = Object.freeze({
        decision: mutation.decision,
        decidedAtUnixMs: now,
      });
    }
    const decoded = decodePrivacyControlsProjectionV1(next);
    try {
      if (!storage || typeof storage.setItem !== "function") throw new Error();
      storage.setItem(PRIVACY_CONTROLS_STORAGE_KEY, JSON.stringify(decoded));
    } catch (_error) {
      return publish(unavailable("write", "storage-write-failed"));
    }
    return publish(decoded);
  }

  const mutate = (input) => mutateAt(input);

  async function reset() {
    const authoritative = read();
    if (authoritative.kind === "unavailable" || authoritative.kind === "missing") {
      return publish(authoritative);
    }
    try {
      if (!storage || typeof storage.removeItem !== "function") throw new Error();
      storage.removeItem(PRIVACY_CONTROLS_STORAGE_KEY);
    } catch (_error) {
      return publish(unavailable("remove", "storage-remove-failed"));
    }
    return publish({ kind: "missing", schemaVersion: DOCUMENT_VERSION });
  }

  async function importLegacyWithdrawal({ decidedAtUtc } = {}) {
    const parsed = Date.parse(String(decidedAtUtc ?? ""));
    const decidedAtUnixMs = Number.isSafeInteger(parsed) && parsed > 0 ? parsed : nowUnixMs();
    return mutateAt({
      kind: "telemetryConsent",
      decision: { kind: "denied", noticeVersion: "legacy-withdrawal.v1" },
    }, decidedAtUnixMs);
  }

  const subscribe = (listener) => {
    if (typeof listener !== "function") throw new TypeError("Privacy listener must be a function.");
    if (disposed) return () => {};
    listeners.add(listener);
    listener(currentState);
    return () => listeners.delete(listener);
  };
  const handleStorage = (event) => {
    if (!disposed && (event?.key === PRIVACY_CONTROLS_STORAGE_KEY || event?.key === null)) load();
  };
  const handlePageShow = (event) => {
    if (!disposed && event?.persisted === true) load();
  };
  eventTarget?.addEventListener?.("storage", handleStorage);
  eventTarget?.addEventListener?.("pageshow", handlePageShow);
  const dispose = () => {
    disposed = true;
    listeners.clear();
    eventTarget?.removeEventListener?.("storage", handleStorage);
    eventTarget?.removeEventListener?.("pageshow", handlePageShow);
  };
  return Object.freeze({
    [LOCAL_BRAND]: DOCUMENT_VERSION,
    dispose,
    importLegacyWithdrawal,
    load,
    mutate,
    reset,
    state,
    subscribe,
  });
}

export function telemetryConsentDecision(
  state,
  defaultMode = "opt-in",
  currentNoticeVersion = TELEMETRY_NOTICE_VERSION,
) {
  const kind = typeof state?.kind === "string" ? state.kind : "uninitialized";
  const receipt = kind === "ready" ? state.telemetryConsent : null;
  const decision = receipt?.decision ?? null;
  const mutable = ["missing", "ready"].includes(kind);
  const currentNoticeAvailable = validConsentNoticeVersion(currentNoticeVersion);
  const currentGrant = decision?.kind === "granted"
    && currentNoticeAvailable
    && decision.noticeVersion === currentNoticeVersion;
  const selection = kind === "ready" ? state.telemetrySelection : disabledTelemetrySelection();
  const selectionApproved = kind === "ready" && state.telemetrySelectionApproved === true;
  const preferenceEnabled = telemetrySelectionActive(selection);
  const runtimeDisabled = defaultMode === "disabled";
  return Object.freeze({
    repositoryKind: kind,
    defaultMode: runtimeDisabled ? "disabled" : "opt-in",
    choice: decision?.kind === "granted" ? "enabled" : decision?.kind === "denied" ? "disabled" : "unset",
    consentGranted: currentGrant,
    preferenceEnabled,
    selection,
    selectionApproved,
    tracesEnabled: !runtimeDisabled && currentGrant && selectionApproved && selection.traces.enabled,
    rumEnabled: !runtimeDisabled && currentGrant && selectionApproved && selection.rum.enabled,
    effectiveEnabled: !runtimeDisabled && currentGrant && selectionApproved && preferenceEnabled,
    source: decision ? "user" : "runtime-default",
    reason: runtimeDisabled
      ? "disabled-by-runtime-policy"
      : !mutable
        ? `consent-${kind}`
        : !currentNoticeAvailable
          ? "consent-notice-unavailable"
          : !decision
            ? "opt-in-required"
            : decision.kind === "denied"
              ? "disabled-by-user"
              : !currentGrant
                ? "consent-renewal-required"
                : !preferenceEnabled
                  ? "telemetry-signals-disabled"
                  : !selectionApproved
                    ? "telemetry-selection-approval-required"
                    : "enabled-by-user",
    canGrant: mutable && currentNoticeAvailable && !runtimeDisabled,
    canWithdraw: kind === "ready" && decision?.kind === "granted",
    canChangeSelection: mutable,
    resetAvailable: kind === "ready" || ["invalid", "unsupportedVersion", "conflict"].includes(kind),
  });
}

export function discordImageConsentDecision(
  state,
  currentNoticeVersion = DISCORD_IMAGE_NOTICE_VERSION,
) {
  const kind = typeof state?.kind === "string" ? state.kind : "uninitialized";
  const decision = kind === "ready" ? state.discordImageConsent?.decision : null;
  const effectiveEnabled = decision?.kind === "granted"
    && decision.noticeVersion === currentNoticeVersion;
  return Object.freeze({
    repositoryKind: kind,
    choice: decision?.kind ?? "unset",
    effectiveEnabled,
    canChange: ["missing", "ready"].includes(kind),
    reason: effectiveEnabled
      ? "enabled-by-user"
      : decision?.kind === "denied"
        ? "disabled-by-user"
        : decision?.kind === "granted"
          ? "consent-renewal-required"
          : kind === "missing"
            ? "opt-in-required"
            : `consent-${kind}`,
  });
}

let localOwner = null;
let localView = null;
let localWindow = null;
let browserOwner = null;
let browserView = null;
let browserWindow = null;

function windowRef() {
  if (!globalThis.window || typeof globalThis.window !== "object") {
    throw new TypeError("Privacy controls require a browser window.");
  }
  return globalThis.window;
}

export function browserLocalPrivacyControlsRepository() {
  const currentWindow = windowRef();
  if (localView && localWindow !== currentWindow) {
    localOwner.dispose();
    localOwner = null;
    localView = null;
  }
  if (!localView) {
    let storage = null;
    try { storage = currentWindow.localStorage; } catch (_error) { storage = null; }
    localOwner = createPrivacyControlsLocalRepository({ storage, eventTarget: currentWindow });
    localOwner.load();
    localView = Object.freeze({
      [BROWSER_LOCAL_BRAND]: DOCUMENT_VERSION,
      importLegacyWithdrawal: localOwner.importLegacyWithdrawal,
      load: localOwner.load,
      mutate: localOwner.mutate,
      reset: localOwner.reset,
      state: localOwner.state,
      subscribe: localOwner.subscribe,
    });
    localWindow = currentWindow;
  }
  return localView;
}

export function browserPrivacyControlsRepository() {
  const currentWindow = windowRef();
  if (browserView && browserWindow !== currentWindow) {
    browserOwner.dispose();
    browserOwner = null;
    browserView = null;
  }
  if (!browserView) {
    browserOwner = createAccountAwarePrivacyControlsRepository({
      localRepository: browserLocalPrivacyControlsRepository(),
      sessionRepository: browserAccountStateSessionRepository(),
      apiBaseUrl: currentWindow.__fishystuffApiBaseUrl,
    });
    browserView = Object.freeze({
      [BROWSER_BRAND]: DOCUMENT_VERSION,
      load: browserOwner.load,
      setTelemetrySelection: (selection) => browserOwner.mutate({
        kind: "telemetrySelection",
        selection: decodeTelemetrySelection(selection),
      }),
      setTelemetrySelectionApproval: (approved) => browserOwner.mutate({
        kind: "telemetrySelectionApproval",
        approved: approved === true,
      }),
      setDiscordImageConsent: (granted) => browserOwner.mutate({
        kind: "discordImageConsent",
        decision: {
          kind: granted ? "granted" : "denied",
          noticeVersion: DISCORD_IMAGE_NOTICE_VERSION,
        },
      }),
      setTelemetryConsent: (granted) => browserOwner.mutate({
        kind: "telemetryConsent",
        decision: {
          kind: granted ? "granted" : "denied",
          noticeVersion: TELEMETRY_NOTICE_VERSION,
        },
      }),
      state: browserOwner.state,
      subscribe: browserOwner.subscribe,
    });
    browserWindow = currentWindow;
  }
  return browserView;
}
